import { ColorName } from '../../../../common/src/constants'
import { aOrAn } from '../../../../common/src/format'
import {
  ThreadBotStatus,
  MessageType,
  ConversationType,
  MessagesWithLinks,
  FetchThreadQuery,
} from '../../../../common/src/types.gen'

export interface Warning {
  text: string
  color: ColorName
  blockSend?: boolean
  link?: string
}

interface Task {
  firm: {
    firmName: string
    headsUpText?: string | null
    englishConversationOnly?: boolean | null
  }
  id: number
  taskType: string
  hasLaunched: boolean
  hasOffboarded: boolean
  isBotDisabled: boolean
}

interface Thread {
  botStatus: ThreadBotStatus
  messageType: MessageType
  messagesWithLinks: MessagesWithLinks
}

export const LLM_SCHEDULING_AGENT_WARNING =
  "When reviewing AI scheduling responses, check for conflicting information in messages and ensure all information is scheduling-related. Verify the AI doesn't address unrelated topics."

export function computeWarnings(
  task: Task,
  thread: FetchThreadQuery['thread'],
  conversationType: ConversationType,
  preferMessageType: MessageType,
  llmAction = false
): Warning[] {
  const warnings = [] as Warning[]
  // always show "heads-up text" from admin for emergencies
  if (task.firm.headsUpText) {
    warnings.push({ text: task.firm.headsUpText, color: 'lemon' })
  }
  // make sure Operator doesn't offer translated for english-only threads
  addTranslationWarnings(task, thread, warnings)
  // make sure we warn operators if the last message was generated by LLM
  addLLMQAWarnings(task, thread, warnings)
  if (llmAction) {
    addLLMSchedulingAgentWarnings(warnings)
  }
  if (thread.isAgentSteppedIn) {
    warnings.push({ text: 'Agent has stepped in. Ignore and archive.', color: 'lemon' })
  }

  if (task.taskType === 'LEASING_PROPERTY' && !task.hasLaunched) {
    warnings.push({ text: 'This property has not launched yet. Ignore, archive.', color: 'coral', blockSend: true })
  } else if (task.taskType === 'LEASING_PROPERTY' && task.hasOffboarded) {
    warnings.push({ text: 'This property is offboarded. Ignore, archive.', color: 'coral', blockSend: true })
  } else if (task.isBotDisabled) {
    warnings.push({ text: 'Bot is disabled. Ignore, archive.', color: 'coral', blockSend: true })
  } else if (thread.botStatus === ThreadBotStatus.FIRM_BLOCKED) {
    warnings.push({ text: 'This contact is blocked.', color: 'coral', blockSend: true })
  } else {
    addBossAgentWarnings(conversationType, task, warnings)
    addMessageTypeWarnings(thread, warnings, preferMessageType)
  }
  return warnings
}

function addTranslationWarnings(task: Task, thread: Thread | FetchThreadQuery['thread'], warnings: Warning[]) {
  const { englishConversationOnly, firmName } = task.firm
  const lastMessageIsNonEnglish =
    thread.messagesWithLinks.messages &&
    thread.messagesWithLinks.messages.find(m => m.isInbound && m.censoredTranslatedShortBody)
  if (englishConversationOnly && lastMessageIsNonEnglish) {
    warnings.push({
      text: 'For legal reasons, English only for ' + firmName + ' leads',
      color: 'lemon',
    })
  }
}

const addLLMSchedulingAgentWarnings = (warnings: Warning[]) => {
  warnings.push({ text: LLM_SCHEDULING_AGENT_WARNING, color: 'coral' })
}

function addLLMQAWarnings(task: Task, thread: Thread | FetchThreadQuery['thread'], warnings: Warning[]) {
  const { id: taskId } = task
  const msgs = thread.messagesWithLinks.messages
  // get last message
  const lastMessage = msgs && msgs[msgs.length - 1]
  // check if last message is an LLM
  const lastMessageIsLLM =
    lastMessage && // Check if lastMessage is defined
    lastMessage.isInbound && // Check if isInbound is defined
    lastMessage.messageLLMInfo && // Check if messageLLMInfo is defined
    lastMessage.messageLLMInfo.length > 0 && // Access length only if messageLLMInfo is defined
    lastMessage.messageLLMInfo.every(question => lastMessage.tags.includes(question))

  const mapping: { [key: string]: string } = {
    Q_TOUR_TYPES_OFFERED: 'Tour Types',
    Q_SHOWING_HOURS: 'Showing Hours',
    Q_OFFICE_PHONE: 'Office Phone',
    Q_APPLICATION_DOCS: 'Application Documents',
    Q_PET_POLICY: 'Pet Policy',
    Q_QUALIFICATIONS: 'Qualifications',
    Q_COSIGNER_POLICY: 'Co-signer Policy',
    Q_MOVE_IN_COST: 'Move In Cost',
    Q_LEASE_TERMS: 'Lease Terms',
    Q_DISCOUNTS: 'Discounts',
    Q_UTILITIES: 'Utilities',
    Q_LAUNDRY: 'Laundry',
    Q_PARKING: 'Parking',
  }

  if (lastMessageIsLLM) {
    const messageLLMInfoList = lastMessage.messageLLMInfo || []
    // convert all elements in messageLLMInfoList to individual strings
    const questionsList = messageLLMInfoList.map(m => m.toString())
    // convert all elements in questionsList to their corresponding question names
    const questions = questionsList.map(q => mapping[q]).join(', ')
    const domainName = window.location.hostname
    warnings.push({
      text: `Lisa is using AI to answer a question about: ${questions}. Please review the Policy on the Task page to verify her response by clicking here.`,
      color: 'coral',
      link: `http://${domainName}/leasing/tasks/task/${taskId}`,
    })
  }
}

function addBossAgentWarnings(conversationType: ConversationType, task: Task, warnings: Warning[]) {
  if (task.taskType !== 'LEASING_FIRM' && ['AGENT', 'BOSS'].includes(conversationType)) {
    warnings.push({ text: 'Please use a firm-level thread.', color: 'coral', blockSend: true })
  } else if (!['AGENT', 'PROSPECT'].includes(conversationType)) {
    warnings.push({ text: 'Careful, it’s ' + aOrAn(conversationType), color: 'lemon' })
  }
}

function addMessageTypeWarnings(
  thread: Thread | FetchThreadQuery['thread'],
  warnings: Warning[],
  preferMessageType: MessageType
) {
  if (thread.messageType === MessageType.VOICE) {
    const text = 'This thread is a landline, we cannot send messages'
    warnings.push({ text, color: 'coral', blockSend: true })
  } else if (thread.messageType !== preferMessageType) {
    const text = 'This contact prefers ' + (preferMessageType === 'EMAIL' ? 'email' : preferMessageType) + '.'
    warnings.push({ text, color: 'lemon' })
  }
}
